import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import authApi from '../services/authApi';

// Create auth context
const AuthContext = createContext();

// Custom hook to use auth context
export const useAuth = () => {
  return useContext(AuthContext);
};

// Auth provider component
export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Function to refresh user data from server
  const refreshUserData = useCallback(async () => {
    try {
      const userData = await authApi.getUserData();
      if (userData) {
        setCurrentUser(userData);
        setUserRole(userData.role);
        setIsAuthenticated(true);
        return userData;
      }
      return null;
    } catch (err) {
      console.error('Error refreshing user data:', err);
      return null;
    }
  }, []);

  // Initialize authentication state
  useEffect(() => {
    const initAuth = async () => {
      try {
        // First check if we have a user in localStorage
        const savedUser = authApi.getCurrentUser();
        
        if (savedUser) {
          setCurrentUser(savedUser);
          setUserRole(savedUser.role);
          setIsAuthenticated(true);
          
          // Verify token validity by fetching user data
          try {
            await refreshUserData();
          } catch (error) {
            console.error('Error verifying token:', error);
            // If token is invalid, log out
            await logoutUser();
          }
        }
      } catch (err) {
        console.error('Auth initialization error:', err);
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, [refreshUserData]);

  // Register a new user
  const registerUser = async (email, password, displayName, role = 'student', school = '', teacherCode = '') => {
    setError('');
    try {
      const response = await authApi.register(email, password, displayName, role, school, teacherCode);
      
      // Don't set current user for registration, require explicit login
      return response.user;
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.message;
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  // Login user
  const loginUser = async (email, password) => {
    setError('');
    try {
      const response = await authApi.login(email, password);
      
      if (response.user) {
        setCurrentUser(response.user);
        setUserRole(response.user.role);
        setIsAuthenticated(true);
      }
      
      return response.user;
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.message;
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  // Logout user
  const logoutUser = async () => {
    setError('');
    try {
      authApi.logout();
      setCurrentUser(null);
      setUserRole(null);
      setIsAuthenticated(false);
    } catch (err) {
      const errorMessage = err.message;
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  // Reset password
  const resetPassword = async (email) => {
    setError('');
    try {
      await authApi.resetPassword(email);
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.message;
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  // Auth context value
  const value = {
    currentUser,
    userRole,
    loading,
    error,
    isAuthenticated,
    registerUser,
    loginUser,
    logoutUser,
    resetPassword,
    refreshUserData,
    isAdmin: userRole === 'admin',
    isTeacher: userRole === 'teacher',
    isStudent: userRole === 'student'
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
